/**
 * SQL Helper utilities for rawQuery
 * Centralizes column name mappings and timezone handling
 */

import { FilterableCardName } from '@/store/models.def';
import { headerTransform } from './csvNames';
import { propertySqlExpressions } from './propertyFunctions';
import accountx from '@/store/modules/accountx';

/**
 * Get the DuckDB table name for a card type
 * Table names follow pattern: t_{accountIdPrefix}_{cardType}
 * @param cardType - The card type (e.g., 'globalCustomers', 'sales')
 * @returns The full table name (e.g., 't_baIu8b14_globalCustomers')
 */
export function getTableName(cardType: FilterableCardName): string {
    const accountId = accountx.currentAccountId || '';
    const accountPrefix = accountId.slice(0, 8);
    return `t_${accountPrefix}_${cardType}`;
}

/**
 * Build SQL filter clause for cross-table queries
 * Handles area filtering and customer filtering via subqueries
 * @param options - Filter options
 * @returns SQL clause starting with " AND ..." or empty string
 */
export function buildCrossTableFilter(options: {
    selectedAreas?: string[];
    selectedCustomers?: string[];
    codeColumn?: string;
}): string {
    const { selectedAreas = [], selectedCustomers = [], codeColumn = COL.CODE } = options;
    const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
    const globalCustomersTable = getTableName('globalCustomers');
    let filterClause = '';

    if (selectedAreas.length > 0) {
        const areaList = selectedAreas.map((a) => `'${escapeStr(a)}'`).join(',');
        filterClause += ` AND ${codeColumn} IN (
            SELECT "CODE" FROM ${globalCustomersTable} WHERE "AREA" IN (${areaList})
        )`;
    }

    if (selectedCustomers.length > 0) {
        const customerList = selectedCustomers.map((c) => `'${escapeStr(c)}'`).join(',');
        filterClause += ` AND ${codeColumn} IN (${customerList})`;
    }

    return filterClause;
}

/**
 * Build SQL filter for new customers (firstDeal within date range)
 * @param options - Filter options for new customer subquery
 * @param codeColumn - Column to match against (default: COL.CODE)
 * @returns SQL clause " AND {codeColumn} IN (SELECT ...)"
 */
export function buildNewCustomerFilter(options: {
    dateRange: [number, number];
    agents?: string[];
    customers?: string[];
    useFirstDealAgent?: boolean;  // true = filter by FIRSTDEALAGENT, false = filter by AGENT
}, codeColumn: string = COL.CODE): string {
    const { dateRange, agents = [], customers = [], useFirstDealAgent = false } = options;
    const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
    const globalCustomersTable = getTableName('globalCustomers');

    const conditions: string[] = [
        `TRY_CAST("FIRSTDEAL" AS BIGINT) >= ${dateRange[0]}`,
        `TRY_CAST("FIRSTDEAL" AS BIGINT) <= ${dateRange[1]}`,
    ];

    if (agents.length > 0) {
        const agentList = agents.map((a) => `'${escapeStr(a)}'`).join(',');
        const agentColumn = useFirstDealAgent ? '"FIRSTDEALAGENT"' : '"AGENT"';
        conditions.push(`${agentColumn} IN (${agentList})`);
    }

    if (customers.length > 0) {
        const customerList = customers.map((c) => `'${escapeStr(c)}'`).join(',');
        conditions.push(`"CODE" IN (${customerList})`);
    }

    return ` AND ${codeColumn} IN (
        SELECT "CODE" FROM ${globalCustomersTable} WHERE ${conditions.join(' AND ')}
    )`;
}

/**
 * Build SQL filter for existing customers (firstDeal before date range start)
 * @param options - Filter options for existing customer subquery
 * @param codeColumn - Column to match against (default: COL.CODE)
 * @returns SQL clause " AND {codeColumn} IN (SELECT ...)"
 */
export function buildExistingCustomerFilter(options: {
    beforeDate: number;
    agents?: string[];
    customers?: string[];
}, codeColumn: string = COL.CODE): string {
    const { beforeDate, agents = [], customers = [] } = options;
    const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
    const globalCustomersTable = getTableName('globalCustomers');

    const conditions: string[] = [
        `TRY_CAST("FIRSTDEAL" AS BIGINT) IS NOT NULL`,
        `TRY_CAST("FIRSTDEAL" AS BIGINT) < ${beforeDate}`,
    ];

    if (agents.length > 0) {
        const agentList = agents.map((a) => `'${escapeStr(a)}'`).join(',');
        conditions.push(`"AGENT" IN (${agentList})`);
    }

    if (customers.length > 0) {
        const customerList = customers.map((c) => `'${escapeStr(c)}'`).join(',');
        conditions.push(`"CODE" IN (${customerList})`);
    }

    return ` AND ${codeColumn} IN (
        SELECT "CODE" FROM ${globalCustomersTable} WHERE ${conditions.join(' AND ')}
    )`;
}

// Timezone offset for GMT+8 (Malaysia) in milliseconds
export const TIMEZONE_OFFSET_MS = 8 * 60 * 60 * 1000; // 28800000ms = 8 hours

// Cache for reverse header lookups (camelCase -> UPPERCASE)
const reverseHeaderCache = new Map<FilterableCardName, Record<string, string>>();

/**
 * Get reverse header transform (camelCase -> UPPERCASE) for a card type
 */
function getReverseHeaders(cardType: FilterableCardName): Record<string, string> {
	if (reverseHeaderCache.has(cardType)) {
		return reverseHeaderCache.get(cardType)!;
	}

	const headers = headerTransform[cardType];
	const reverse: Record<string, string> = {};

	if (headers) {
		for (const upperKey in headers) {
			if (headers.hasOwnProperty(upperKey)) {
				const camelKey = headers[upperKey];
				if (camelKey !== '_') {
					reverse[camelKey] = upperKey;
				}
			}
		}
	}

	reverseHeaderCache.set(cardType, reverse);
	return reverse;
}

/**
 * Get UPPERCASE column name from camelCase property
 * @param cardType - The card type (e.g., 'sales', 'stockTrans')
 * @param camelKey - The camelCase property name (e.g., 'amount', 'date')
 * @returns The UPPERCASE column name (e.g., 'AMOUNT', 'DOCDATE')
 */
export function getColumnName(cardType: FilterableCardName, camelKey: string): string {
	const reverse = getReverseHeaders(cardType);
	return reverse[camelKey] || camelKey.toUpperCase();
}

/**
 * Get quoted UPPERCASE column name for SQL
 * @param cardType - The card type
 * @param camelKey - The camelCase property name
 * @returns The quoted column name (e.g., '"AMOUNT"', '"DOCDATE"')
 */
export function getQuotedColumn(cardType: FilterableCardName, camelKey: string): string {
	return `"${getColumnName(cardType, camelKey)}"`;
}

/**
 * Get SQL column reference - handles both regular columns and computed properties
 * For computed properties, returns the SQL expression
 * For regular columns, returns the quoted column name
 * @param cardType - The card type
 * @param camelKey - The camelCase property name
 * @returns SQL expression or quoted column name
 */
export function getSqlColumnRef(cardType: FilterableCardName, camelKey: string): string {
	// Handle table-dependent computed properties where column names vary per table
	// e.g., sales has "TAX" but customerTrades has "LOCALTAXAMT"
	if (camelKey === 'amountIncludeTax') {
		const amtCol = getQuotedColumn(cardType, 'amount');
		const taxCol = resolveTaxColumnForTable(cardType);
		return `(TRY_CAST(${amtCol} AS DOUBLE) + COALESCE(TRY_CAST(${taxCol} AS DOUBLE), 0))`;
	}
	if (camelKey === 'profitIncludeTax') {
		const amtCol = getQuotedColumn(cardType, 'amount');
		const taxCol = resolveTaxColumnForTable(cardType);
		const costCol = getQuotedColumn(cardType, 'cost');
		return `(TRY_CAST(${amtCol} AS DOUBLE) + TRY_CAST(${taxCol} AS DOUBLE) - TRY_CAST(${costCol} AS DOUBLE))`;
	}

	// Check if it's a computed property with a SQL expression
	const sqlExpr = propertySqlExpressions[camelKey];
	if (sqlExpr) {
		return sqlExpr;
	}
	// Regular column
	return getQuotedColumn(cardType, camelKey);
}

/**
 * Check if a property is a computed property
 */
export function isComputedProperty(camelKey: string): boolean {
	return !!propertySqlExpressions[camelKey]
		|| camelKey === 'amountIncludeTax' || camelKey === 'profitIncludeTax';
}

/**
 * Resolve the correct tax column name for a given table.
 * SL tables (sales, creditNotes, etc.) use "TAX" mapped as 'tax'.
 * AR/PH tables (customerTrades, supplierTrades, etc.) use "LOCALTAXAMT" mapped as 'taxAmount'.
 */
function resolveTaxColumnForTable(cardType: FilterableCardName): string {
	const headers = headerTransform[cardType];
	if (headers) {
		// Check if the table maps a column to 'taxAmount' (AR/PH pattern)
		for (const upperKey in headers) {
			if (headers.hasOwnProperty(upperKey) && headers[upperKey] === 'taxAmount') {
				return `"${upperKey}"`;
			}
		}
	}
	// Default: SL pattern with "TAX" column
	return getQuotedColumn(cardType, 'tax');
}

/**
 * Common column names in parquet files (UPPERCASE)
 * These are the actual column names stored in parquet/DuckDB
 * The camelCase equivalents are shown in comments
 */
export const COL = {
    // Date columns
    DOCDATE: '"DOCDATE"',       // -> date
    POSTDATE: '"POSTDATE"',     // -> date (for some tables)
    DUEDATE: '"DUEDATE"',       // -> dueDate

    // Common fields
    DOCTYPE: '"DOCTYPE"',       // -> docType
    DOCNO: '"DOCNO"',           // -> docNo
    AMOUNT: '"AMOUNT"',         // -> amount
    LOCALAMOUNT: '"LOCALAMOUNT"', // -> amount (for trades tables)
    PROFIT: '"PROFIT"',         // -> profit
    CODE: '"CODE"',             // -> code
    AGENT: '"AGENT"',           // -> agent
    ITEMCODE: '"ITEMCODE"',     // -> itemCode
    STOCKGROUP: '"STOCKGROUP"', // -> stockGroup
    QUANTITY: '"QUANTITY"',     // -> quantity
    QTY: '"QTY"',               // -> quantity (alternate)
    TAX: '"TAX"',               // -> tax
    COST: '"COST"',             // -> cost
    DISCOUNT: '"DISCOUNT"',     // -> discount

    // Customer/Supplier aging columns
    LOCALDOCAMT: '"LOCALDOCAMT"',       // -> amount (for customerIvDns/supplierPiSds)
    LOCALDOCOSAMT: '"LOCALDOCOSAMT"',   // -> osAmount
    KOLOCALAMT: '"KOLOCALAMT"',         // -> amount (for customerKos/supplierKos)
    KOGAINLOSS: '"KOGAINLOSS"',         // -> gainLoss
    FROMDOCDATE: '"FROMDOCDATE"',       // -> koDate
    TODOCDATE: '"TODOCDATE"',           // -> date (for Kos tables)
    TODOCDUEDATE: '"TODOCDUEDATE"',     // -> dueDate (for Kos tables)
    UNAPPLIEDAMT: '"UNAPPLIEDAMT"',     // -> unappliedAmount
    LOCALUNAPPLIEDAMT: '"LOCALUNAPPLIEDAMT"', // -> lUnappliedAmount
} as const;

/**
 * Generate SQL for extracting year from a date column with timezone adjustment
 * @param dateCol - Column name (e.g., COL.DOCDATE or '"DOCDATE"')
 * @param alias - Optional alias for the result (default: 'year')
 */
export function sqlExtractYear(dateCol: string, alias: string = 'year'): string {
    return `EXTRACT(YEAR FROM EPOCH_MS(TRY_CAST(${dateCol} AS BIGINT) + ${TIMEZONE_OFFSET_MS})) as ${alias}`;
}

/**
 * Generate SQL for extracting month from a date column with timezone adjustment
 * @param dateCol - Column name (e.g., COL.DOCDATE or '"DOCDATE"')
 * @param alias - Optional alias for the result (default: 'month')
 */
export function sqlExtractMonth(dateCol: string, alias: string = 'month'): string {
    return `EXTRACT(MONTH FROM EPOCH_MS(TRY_CAST(${dateCol} AS BIGINT) + ${TIMEZONE_OFFSET_MS})) as ${alias}`;
}

/**
 * Generate SQL for extracting day from a date column with timezone adjustment
 * @param dateCol - Column name (e.g., COL.DOCDATE or '"DOCDATE"')
 * @param alias - Optional alias for the result (default: 'day')
 */
export function sqlExtractDay(dateCol: string, alias: string = 'day'): string {
    return `EXTRACT(DAY FROM EPOCH_MS(TRY_CAST(${dateCol} AS BIGINT) + ${TIMEZONE_OFFSET_MS})) as ${alias}`;
}

/**
 * Generate SQL for year, month extraction (commonly used for GROUP BY)
 * @param dateCol - Column name (e.g., COL.DOCDATE)
 */
export function sqlExtractYearMonth(dateCol: string): string {
    return `${sqlExtractYear(dateCol)}, ${sqlExtractMonth(dateCol)}`;
}

/**
 * Generate SQL for CASE WHEN sum by docType
 * @param docTypes - Array of doc types to match
 * @param amountCol - Column to sum (e.g., COL.AMOUNT)
 * @param alias - Result alias
 */
export function sqlSumByDocType(docTypes: string[], amountCol: string, alias: string): string {
    const typeList = docTypes.map((t) => `'${t}'`).join(', ');
    return `SUM(CASE WHEN ${COL.DOCTYPE} IN (${typeList}) ` +
        `THEN TRY_CAST(${amountCol} AS DOUBLE) ELSE 0 END) as ${alias}`;
}

/**
 * Generate SQL for simple sum with DOUBLE cast
 * @param col - Column to sum
 * @param alias - Result alias
 */
export function sqlSum(col: string, alias: string): string {
    return `SUM(TRY_CAST(${col} AS DOUBLE)) as ${alias}`;
}

/**
 * Generate SQL CASE WHEN expressions for aging buckets
 * Uses days since asOfDate to bucket amounts
 * @param dateCol - Date column to calculate age from (e.g., COL.DOCDATE)
 * @param amountCol - Amount column to sum
 * @param asOfDate - Reference date in milliseconds
 * @param bucketDays - Array of bucket boundaries (default: [30, 60, 90, 120])
 * @returns Object with bucket SQL expressions
 */
export function sqlAgingBuckets(
    dateCol: string,
    amountCol: string,
    asOfDate: number,
    bucketDays: number[] = [30, 60, 90, 120],
): { [key: string]: string } {
    const msPerDay = 24 * 60 * 60 * 1000;
    const buckets: { [key: string]: string } = {};

    // Calculate days ago: (asOfDate - dateCol) / msPerDay
    const daysAgo = `((${asOfDate} - TRY_CAST(${dateCol} AS BIGINT)) / ${msPerDay})`;
    const amountExpr = `TRY_CAST(${amountCol} AS DOUBLE)`;

    // Current bucket (0 to first boundary)
    buckets.current = `SUM(CASE WHEN ${daysAgo} >= 0 AND ${daysAgo} < ${bucketDays[0]} THEN ${amountExpr} ELSE 0 END) as bucket_current`;

    // Middle buckets
    for (let i = 0; i < bucketDays.length - 1; i++) {
        const start = bucketDays[i];
        const end = bucketDays[i + 1];
        buckets[`over${start}`] = `SUM(CASE WHEN ${daysAgo} >= ${start} AND ${daysAgo} < ${end} THEN ${amountExpr} ELSE 0 END) as bucket_${start}`;
    }

    // Last bucket (over last boundary)
    const lastBoundary = bucketDays[bucketDays.length - 1];
    buckets[`over${lastBoundary}`] = `SUM(CASE WHEN ${daysAgo} >= ${lastBoundary} THEN ${amountExpr} ELSE 0 END) as bucket_${lastBoundary}`;

    return buckets;
}

/**
 * Generate all aging bucket SQL expressions as a single string for SELECT
 * @param dateCol - Date column to calculate age from
 * @param amountCol - Amount column to sum
 * @param asOfDate - Reference date in milliseconds
 * @param bucketDays - Array of bucket boundaries (default: [30, 60, 90, 120])
 * @returns SQL string with all bucket expressions
 */
export function sqlAgingBucketsSelect(
    dateCol: string,
    amountCol: string,
    asOfDate: number,
    bucketDays: number[] = [30, 60, 90, 120],
): string {
    const buckets = sqlAgingBuckets(dateCol, amountCol, asOfDate, bucketDays);
    return Object.values(buckets).join(',\n\t\t\t\t');
}
